import React, { useState, useEffect, useMemo } from 'react';
import { Routes, Route } from 'react-router-dom';
import { signInWithPopup, GoogleAuthProvider, signOut, onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot, updateDoc, arrayUnion, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from './firebase';
import Stats from './Stats';

// --- Preview Configuration ---
const PREVIEW_MODE = false; // Set to true to force show happy hour for testing
const TEST_DATE = null; // Set to "YYYY-MM-DD" (e.g., "2026-01-06") to test a specific date, or null for normal operation

// --- Happy Hour Configuration ---
const happyHourConfig = {
    // Set this to the date you want the happy hour to appear (YYYY-MM-DD format)
    // Set to null to disable happy hour
    scheduledDate: "2025-10-09", // Set to your desired happy hour date
    location: {
        name: "Bankhead Brewpub",
        description: "Come join us for an unofficial happy hour as David visits from Minnesota!",
        time: "5:30 PM - 7:00 PM",
        parking: "Street parking available next to the brewery.",
        capacity: "Medium. There are several tables that can support 4-6 that we can connect or sit near each other.",
        address: "611 University Dr, Fort Worth, TX 76107"
    }
};

// --- Pre-defined Coffee Shop Locations ---
const coffeeShops = [
   {
      name: "Avoca Coffee Roasters (Foch St)",
      description: "Single-origin, house-roasted beans yield coffee beverages plus baked goods and tacos in a casual space.",
      plugs: "Yes. At every table.",
      parking: "Park in front or back of the building. Get validated inside for up to 3 hours.",
      capacity: "Large. Benches seat up to 4 people. Up to 8 could sit comfortably between two benches.",
      address: "835 Foch Street, Fort Worth, TX 76107",
      time: ["morning", "afternoon"], 
   },
   /*{
      name: "Avoca Coffee Roasters (Magnolia Ave)",
      description: "Single-origin, house-roasted beans yield coffee beverages plus baked goods and tacos in a casual space.",
      plugs: "Yes. At tables along the walls.",
      parking: "Park in front of the building or along the street for free.",
      capacity: "Medium. There are several tables that can support 4, with most supporting 2-3.",
      address: "1311 W. Magnolia Ave, Fort Worth, TX 76104",
   },*/
   {
      name: "Crude Coffee Bar",
      description: "A cozy and modern coffee shop known for its quality brews, homemade pastries and friendly atmosphere.",
      plugs: "Yes. At the bar in the front and the tables along the wall.",
      parking: "Free parking along the street. Do not park in the business across from the coffee shop.",
      capacity: "Small. Seats accomodate 2 people, up to 4 if side by side. One 6 person table.",
      address: "804 S Main St #120, Fort Worth, TX 76104",
   },
   {
      name: "Match Point Coffee",
      description: "Tennis-themed coffee shop with a relaxed atmosphere, a simple coffee menu and unique taco options with a private area for podcasting.",
      plugs: "Yes. Along the wall.",
      parking: "Plenty of free parking in front of the business.",
      capacity: "Small. Seats accomodate 2 people with a few tables for 4.",
      address: "3618 W 7th St B, Fort Worth, TX 76107",
   },
   /*{
      name: "Summer Moon Coffee (South Main)",
      description: "Quirky chain serving wood-fired coffee drinks, plus breakfast tacos, pastries & other light bites.",
      plugs: "Yes. Along the wall and in the center, shared table.",
      parking: "Free parking in back of building in the parking lot or on the street.",
      capacity: "Medium. Seats accomodate 2-4 with a long table that could hold 6-8 if arriving early.",
      address: "625 S Main St, Fort Worth, TX 76104",
   },*/
   {
      name: "The Legal Cafe",
      description: "Downtown coffee shop with quality coffee, affordable food and a quiet environment.",
      plugs: "Yes. At every table.",
      parking: "On the left side of the building there are 5 spots labeled Client Parking that are free to use.",
      capacity: "Medium. Seats accomodate 2-4 people with a long table that seats up to 6 if you arrive early.",
      address: "114 Main St, Fort Worth, TX 76102",
   },
   {
      name: "Buon Giorno Coffee",
      description: "Counter-serve cafe with an Italian slant, offering specialty coffees, plus pastries & desserts.",
      plugs: "Yes. At the bar and along the wall.",
      parking: "Park in the garage across 6th street. Ask for the extended validation from the barista for 6 hours free parking.",
      capacity: "Large. Tables seat 2-4 people. Up to 5 could sit around the bar. Combine tables in the lobby for up to 8 people.",
      address: "500 W 7th St Suite 140, Fort Worth, TX 76102",
   },
   {
      name: "Cherry Coffee",
      description: "Cozy coffee shop with a homey atmosphere and a focus on quality coffee, light bites and tacos with a friendly staff.",
      plugs: "Yes. At most seats except for outside.",
      parking: "Parking is free on the street or paid in the dirt lot across the street.",
      capacity: "Small. Tables seat 2-3 people max. Outdoor seating available for up to 4.",
      address: "1121 W Magnolia Ave, Fort Worth, TX 76104",
   },
   {
      name: "Roots Coffeehouse",
      description: "Well-lit, modern coffee shop with quality coffee, light bites (including avocado toast) and near our new office!",
      plugs: "Yes. At most seats.",
      parking: "There are 2 spots next to the building otherwise parking on the street is free.",
      capacity: "Small. Tables can seat 2-4 people max. The conference table in the back can support up to 6.",
      address: "400 Bryan Ave, Fort Worth, TX 76104",
   },
   {
      name: "Sons of Liberty Coffee",
      description: "Counter-serve cafe with an Italian slant, offering specialty coffees, plus pastries & desserts.",
      plugs: "Yes. At every table along the wall and at the long table in the center.",
      parking: "Street parking is paid during the week. Park in the garage behind the shop for 2 hours free.",
      capacity: "Medium. Long table seats up to 6 people. Tables along the wall seat 2 but could fit 4 if side by side.",
      address: "500 W 7th St Suite 140, Fort Worth, TX 76102",
  },
  {
      name: "Roy Pope Grocery",
      description: "Grocery store with a outdoor patio and indoor seating with coffee, tacos and afternoon wine. IMPORTANT: Wifi is not great here for meetings.",
      plugs: "Yes. Along the walls outside and inside.",
      parking: "Free parking everywhere.",
      capacity: "Small. Tables seat 2-4 people but limited plugs restrict long-term use.",
      address: "2300 Merrick St, Fort Worth, TX 76107",
   },
   {
      name: "Marché Bleu",
      description: "French-style cafe with a modern, light-filled space with patio seating, tables inside and a few bar seats.",
      plugs: "Yes. There are plugs along the bar and at the tables.",
      parking: "Free parking on the street or in any of the garages in Clearfork.",
      capacity: "Small. Most areas can support 2-4 people. There are 8-10 bar seats inside, 4 tables and a large patio if the weather is nice.",
      address: "5256 Marathon Ave, Fort Worth, TX 76107",
   },
   /*{
      name: "Black Coffee",
      description: "Coffee shop near Texas Wesleyan University with a modern, light-filled space with patio seating, tables inside and a few bar seats.",
      plugs: "Yes. There are a few plugs avaiable on the walls.",
      parking: "Free parking behind the building.",
      capacity: "Small. Most areas can support 2-4 people with a few patio seats outside and some bar seats along the wall.",
      address: "1417 Vaughn Blvd, Fort Worth, TX 76105",
      time: ["morning"],
   },*/
   {
      name: "Branch & Bean Coffee and Roastery",
      description: "Located inside Maple Branch Brewery, this is a new coffee shop serving all your favorites with plenty of space to sit indoors or outdoors.",
      plugs: "Yes. Along the walls.",
      parking: "Free parking on the street or in the parking lot next to the brewery except for the Tesla charging spots.",
      capacity: "Large. There are many long bench tables that can support upwards of 16 people per table.",
      address: "2628 Whitmore Street, Fort Worth, Texas 76107",
   }
];

// --- Helper Functions ---
const getTodayDateString = () => {
    // If TEST_DATE is set, use that instead of the actual date
    if (TEST_DATE) {
        return TEST_DATE;
    }
    
    const now = new Date();
    
    // Get current time in CST
    const cstTime = new Date(now.toLocaleString("en-US", {timeZone: "America/Chicago"}));
    
    // If it's before 9 PM CST, use today's date
    // If it's 9 PM CST or later, use tomorrow's date (so people can see next day's locations)
    if (cstTime.getHours() >= 21) { // 9 PM = 21:00
        // Get tomorrow's date by adding one day to the CST date
        const tomorrowCST = new Date(cstTime);
        tomorrowCST.setDate(tomorrowCST.getDate() + 1);
        
        // Format the date manually to avoid timezone conversion issues
        const year = tomorrowCST.getFullYear();
        const month = String(tomorrowCST.getMonth() + 1).padStart(2, '0');
        const day = String(tomorrowCST.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }
    
    // Return today's date in CST
    const year = cstTime.getFullYear();
    const month = String(cstTime.getMonth() + 1).padStart(2, '0');
    const day = String(cstTime.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

// Check if a given date is a holiday
const isHoliday = (dateString) => {
    const date = new Date(dateString + 'T00:00:00');
    const month = date.getMonth() + 1; // getMonth() returns 0-11
    const day = date.getDate();
    const year = date.getFullYear();
    
    // Labor Day (9/1/2025)
    if (month === 9 && day === 1 && year === 2025) {
        return true;
    }
    
    // Thanksgiving (11/27 and 11/28)
    if (month === 11 && (day === 27 || day === 28)) {
        return true;
    }
    
    // Christmas Break (12/24 through 1/2)
    if (month === 12 && day >= 24) {
        return true;
    }
    if (month === 1 && day <= 2) {
        return true;
    }
    
    return false;
};

// Check if today is the scheduled happy hour day
const isHappyHourDay = (dateString) => {
    return PREVIEW_MODE || happyHourConfig.scheduledDate === dateString;
};

// Get the week start date (Monday) for a given date
const getWeekStart = (dateString) => {
    const date = new Date(dateString + 'T00:00:00');
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1); // Adjust for Sunday
    const monday = new Date(date.setDate(diff));
    return monday.toISOString().split('T')[0];
};

// Helper function to check if a shop is available for a given time slot
// time can be: undefined/null (available for both), "morning", "afternoon", or ["morning", "afternoon"]
const isShopAvailableForTime = (shop, timeSlot) => {
    // If shop has no time restriction, it's available for both
    if (!shop.time) {
        return true;
    }
    
    // If time is a string, convert to array for consistent checking
    const shopTimes = Array.isArray(shop.time) ? shop.time : [shop.time];
    
    // Check if the timeSlot is in the shop's available times
    return shopTimes.includes(timeSlot);
};

// Generate coffee shop locations for a specific day using weekly rotation
const generateDailyLocations = (dateString, usedShopsThisWeek = []) => {
    const weekStart = getWeekStart(dateString);
    
    // Create a seed based on the week start date for consistent weekly randomization
    const weekSeed = weekStart.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    
    // Create a shuffled array of coffee shops for this week
    const shuffledShops = [...coffeeShops];
    for (let i = shuffledShops.length - 1; i > 0; i--) {
        const j = (weekSeed + i) % (i + 1);
        [shuffledShops[i], shuffledShops[j]] = [shuffledShops[j], shuffledShops[i]];
    }
    
    // Get day of week (0 = Monday, 1 = Tuesday, etc.) for day-specific selection
    const date = new Date(dateString + 'T00:00:00');
    const dayOfWeek = date.getDay();
    const dayOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Convert Sunday (0) to 6, Mon-Fri are 0-4
    
    // Filter shops based on time availability and whether they've been used this week
    const getAvailableShopsForTime = (timeSlot) => {
        return shuffledShops.filter(shop => {
            // Check if shop is available for this time slot
            if (!isShopAvailableForTime(shop, timeSlot)) {
                return false;
            }
            // Check if shop hasn't been used this week
            if (usedShopsThisWeek.length > 0) {
                return !usedShopsThisWeek.some(used => used.name === shop.name);
            }
            return true;
        });
    };
    
    // Get available shops for morning and afternoon (for fallback if no unused shops available)
    const availableMorningShops = getAvailableShopsForTime('morning');
    const availableAfternoonShops = getAvailableShopsForTime('afternoon');
    
    // If no shops available for a time slot, fall back to all shops (including used ones)
    const morningShops = availableMorningShops.length > 0 
        ? availableMorningShops 
        : shuffledShops.filter(shop => isShopAvailableForTime(shop, 'morning'));
    const afternoonShops = availableAfternoonShops.length > 0 
        ? availableAfternoonShops 
        : shuffledShops.filter(shop => isShopAvailableForTime(shop, 'afternoon'));
    
    // Select shop for morning with day-specific offset
    // Monday gets index 0, Tuesday gets index 1, etc. from the available list
    let morningLocation = null;
    if (availableMorningShops.length > 0) {
        // Use day offset to pick different shops for each day
        const morningIndex = dayOffset % availableMorningShops.length;
        morningLocation = availableMorningShops[morningIndex];
    }
    
    // Fallback: if no unused shops available, use any shop available for morning
    if (!morningLocation && morningShops.length > 0) {
        const morningIndex = dayOffset % morningShops.length;
        morningLocation = morningShops[morningIndex];
    }
    
    // Select shop for afternoon with day-specific offset
    // Make sure it's different from morning location
    let afternoonLocation = null;
    if (availableAfternoonShops.length > 0) {
        // Filter out the morning location
        const afternoonOptions = availableAfternoonShops.filter(shop => shop.name !== morningLocation?.name);
        if (afternoonOptions.length > 0) {
            const afternoonIndex = dayOffset % afternoonOptions.length;
            afternoonLocation = afternoonOptions[afternoonIndex];
        }
    }
    
    // Fallback: if no unused shops available, use any shop available for afternoon
    if (!afternoonLocation && afternoonShops.length > 0) {
        const afternoonOptions = afternoonShops.filter(shop => shop.name !== morningLocation?.name);
        if (afternoonOptions.length > 0) {
            const afternoonIndex = dayOffset % afternoonOptions.length;
            afternoonLocation = afternoonOptions[afternoonIndex];
        } else if (afternoonShops.length > 0) {
            // Last resort: pick any afternoon shop even if it's the same as morning
            const afternoonIndex = dayOffset % afternoonShops.length;
            afternoonLocation = afternoonShops[afternoonIndex];
        }
    }
    
    return {
        morningLocation: morningLocation,
        afternoonLocation: afternoonLocation,
        weekStart: weekStart
    };
};

// Get all used coffee shops from the current week
// NOTE: Excludes shops from manually overridden days to prevent interference with the seed
const getUsedShopsThisWeek = async (db, weekStart) => {
    try {
        const usedShops = [];
        const weekStartDate = new Date(weekStart + 'T00:00:00');
        const weekEndDate = new Date(weekStart + 'T00:00:00');
        weekEndDate.setDate(weekEndDate.getDate() + 6); // End of week (Sunday)
        
        // Generate all dates in the week
        const weekDates = [];
        for (let i = 0; i < 7; i++) {
            const date = new Date(weekStartDate);
            date.setDate(date.getDate() + i);
            weekDates.push(date.toISOString().split('T')[0]);
        }
        
        // Check each date for existing documents and collect used shops
        // Skip manually overridden days so they don't affect the weekly seed
        // Only check work from home days (Wed/Fri)
        for (const dateStr of weekDates) {
            // Skip if not a work from home day
            if (!isWorkFromHomeDay(dateStr)) {
                continue;
            }
            
            const docRef = doc(db, 'daily_locations', dateStr);
            const docSnap = await getDoc(docRef);
            
            if (docSnap.exists()) {
                const data = docSnap.data();
                
                // Skip this day if it was manually overridden
                if (data.manualOverride) {
                    continue;
                }
                
                // Check new structure first, then fall back to old structure
                if (data.dailyLocation) {
                    usedShops.push(data.dailyLocation);
                } else if (data.afternoonLocation) {
                    usedShops.push(data.afternoonLocation);
                } else if (data.morningLocation) {
                    usedShops.push(data.morningLocation);
                }
            }
        }
        
        return usedShops;
    } catch (error) {
        console.error("Error getting used shops this week:", error);
        return [];
    }
};

// Helper function to convert time string to sortable value
const timeStringToMinutes = (timeString) => {
    if (!timeString) return 0;
    
    const [time, period] = timeString.split(' ');
    const [hours, minutes] = time.split(':').map(Number);
    
    let totalMinutes = hours * 60 + minutes;
    
    // Convert to 24-hour format for proper sorting
    if (period === 'PM' && hours !== 12) {
        totalMinutes += 12 * 60;
    } else if (period === 'AM' && hours === 12) {
        totalMinutes = minutes; // 12:00 AM = 0 minutes
    }
    
    return totalMinutes;
};

const generateTimeOptions = (session) => {
    const times = [];
    
    // For work from home days: 8:30 AM to 5:00 PM (with lunch break 12-1 PM)
    if (session === 'workFromHome') {
        for (let hour = 8; hour <= 17; hour++) {
            for (let minute = 0; minute < 60; minute += 30) {
                // Skip times before 8:30 AM
                if (hour === 8 && minute < 30) continue;
                // Skip times after 5:00 PM
                if (hour === 17 && minute > 0) continue;
                // Skip lunch break (12:00 PM and 12:30 PM)
                if (hour === 12) continue;
                
                const time = new Date();
                time.setHours(hour, minute, 0, 0);
                const timeString = time.toLocaleTimeString('en-US', { 
                    hour: 'numeric', 
                    minute: '2-digit',
                    hour12: true 
                });
                times.push(timeString);
            }
        }
    } else if (session === 'morning') {
        // Morning session: 8:30 AM to 12:00 PM
        for (let hour = 8; hour <= 12; hour++) {
            for (let minute = 0; minute < 60; minute += 30) {
                // Skip times before 8:30 AM
                if (hour === 8 && minute < 30) continue;
                // Skip times after 12:00 PM
                if (hour === 12 && minute > 0) continue;
                
                const time = new Date();
                time.setHours(hour, minute, 0, 0);
                const timeString = time.toLocaleTimeString('en-US', { 
                    hour: 'numeric', 
                    minute: '2-digit',
                    hour12: true 
                });
                times.push(timeString);
            }
        }
    } else if (session === 'afternoon') {
        // Afternoon session: 1:00 PM to 5:30 PM
        for (let hour = 13; hour <= 17; hour++) {
            for (let minute = 0; minute < 60; minute += 30) {
                // Skip times after 5:30 PM
                if (hour === 17 && minute > 30) continue;
                
                const time = new Date();
                time.setHours(hour, minute, 0, 0);
                const timeString = time.toLocaleTimeString('en-US', { 
                    hour: 'numeric', 
                    minute: '2-digit',
                    hour12: true 
                });
                times.push(timeString);
            }
        }
    }
    
    return times;
};

// Office location for in-office days
const officeLocation = {
    name: "Schaefer Advertising",
    description: "Our office in the heart of Fort Worth. We'll see you in our new home today!",
    address: "207 E Broadway Ave, Fort Worth, TX 76104",
    parking: "We have a parking lot in the back of the building. Free street parking available on Broadway Ave and surrounding streets but be mindful of the parking signs. Tickets are real!",
    capacity: "Plenty of space for the whole team!"
};

// Check if a date is a work from home day (Wednesday or Friday)
const isWorkFromHomeDay = (dateString) => {
    const date = new Date(dateString + 'T00:00:00');
    const dayOfWeek = date.getDay();
    // 3 = Wednesday, 5 = Friday
    return dayOfWeek === 3 || dayOfWeek === 5;
};

// --- Main Home Component ---
function Home() {

    const [user, setUser] = useState(null);
    const [isAuthReady, setIsAuthReady] = useState(false);
    
    const [dailyLocation, setDailyLocation] = useState(null);
    const [isOfficeDay, setIsOfficeDay] = useState(false);
    const [attendees, setAttendees] = useState([]);
    const [happyHourAttendees, setHappyHourAttendees] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showTimeModal, setShowTimeModal] = useState(false);
    const [selectedTime, setSelectedTime] = useState('');
    const [selectedSession, setSelectedSession] = useState('');
    const [currentDateStr, setCurrentDateStr] = useState('');

    // --- Firebase Initialization ---
    useEffect(() => {
        if (auth && db) {
            const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
                setUser(currentUser);
                setIsAuthReady(true);
                // Clear errors when authentication state changes successfully
                if (currentUser) {
                    setError(null);
                }
            });

            // Skip anonymous authentication for now - it's causing errors
            // Users can still view the daily location and log in manually if needed
            console.log("Skipping anonymous authentication");
            setIsLoading(false);
            return () => unsubscribe();
        } else {
            setIsLoading(false);
            setError("Firebase configuration is missing.");
        }
            }, []);

    // --- Daily Location Logic ---
    useEffect(() => {
        if (!db) return;

        // Expose utility functions to window for console access
        if (typeof window !== 'undefined') {
            window.setCustomLocation = async (dateString, shopName) => {
                if (!db) {
                    console.error('Firebase db not available');
                    return;
                }
                
                if (!dateString || !dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
                    console.error('Invalid date format. Use YYYY-MM-DD format, e.g., "2025-12-18"');
                    return;
                }
                
                // Find the coffee shop
                const shop = coffeeShops.find(s => s.name === shopName);
                
                if (!shop) {
                    console.error(`Shop "${shopName}" not found. Available shops:`);
                    coffeeShops.forEach(s => console.log(`  - ${s.name}`));
                    return;
                }
                
                const docRef = doc(db, 'daily_locations', dateString);
                
                try {
                    // Get existing document to preserve attendees
                    const docSnap = await getDoc(docRef);
                    const existingData = docSnap.exists() ? docSnap.data() : {};
                    
                    await setDoc(docRef, {
                        dailyLocation: shop,
                        attendees: existingData.attendees || [],
                        happyHourAttendees: existingData.happyHourAttendees || [],
                        weekStart: getWeekStart(dateString),
                        updatedAt: new Date().toISOString(),
                        manualOverride: true
                    }, { merge: true });
                    
                    console.log(`✅ Successfully updated location for ${dateString}:`);
                    console.log(`   ☕ Location: ${shop.name}`);
                    console.log('\nRefresh the page to see the changes');
                } catch (error) {
                    console.error('Error setting custom location:', error);
                }
            };
            
            window.listCoffeeShops = () => {
                console.log('\nAvailable Coffee Shops:\n');
                coffeeShops.forEach((shop, index) => {
                    console.log(`${index + 1}. ${shop.name}`);
                });
                console.log('\nNote: Use exact names (copy/paste recommended)\n');
            };
            
            window.checkTodayMetadata = async () => {
                if (!db) {
                    console.error('Firebase db not available');
                    return;
                }
                
                const todayStr = getTodayDateString();
                const docRef = doc(db, 'daily_locations', todayStr);
                
                try {
                    const docSnap = await getDoc(docRef);
                    
                    if (docSnap.exists()) {
                        const data = docSnap.data();
                        console.log('\n📊 Current Document Data for ' + todayStr + ':');
                        console.log('  Location:', data.dailyLocation?.name || data.afternoonLocation?.name || 'Not set');
                        console.log('  Created At:', data.createdAt || 'N/A');
                        console.log('  Updated At:', data.updatedAt || 'N/A');
                        console.log('  Auto Generated:', data.autoGenerated || false);
                        console.log('  Manual Override:', data.manualOverride || false);
                        console.log('  Week Start:', data.weekStart || 'N/A');
                        console.log('  Coffee Attendees:', data.attendees?.length || 0);
                        if (data.attendees && data.attendees.length > 0) {
                            console.log('  Coffee RSVPs:');
                            data.attendees.forEach(a => {
                                console.log(`    - ${a.firstName} (${a.session}) at ${a.timestamp}`);
                            });
                        }
                        console.log('  Happy Hour Attendees:', data.happyHourAttendees?.length || 0);
                        console.log('\n');
                    } else {
                        console.log(`No document found for ${todayStr}`);
                    }
                } catch (error) {
                    console.error('Error checking metadata:', error);
                }
            };
            
            window.viewThisWeek = async () => {
                if (!db) {
                    console.error('Firebase db not available');
                    return;
                }
                
                const todayStr = getTodayDateString();
                const weekStart = getWeekStart(todayStr);
                const weekStartDate = new Date(weekStart + 'T00:00:00');
                
                console.log(`\n☕ Locations for Week of ${weekStart}:\n`);
                
                // Check weekdays only (Monday-Friday)
                for (let i = 0; i < 5; i++) {
                    const date = new Date(weekStartDate);
                    date.setDate(date.getDate() + i);
                    const dateStr = date.toISOString().split('T')[0];
                    const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
                    const isWFH = isWorkFromHomeDay(dateStr);
                    
                    const docRef = doc(db, 'daily_locations', dateStr);
                    
                    try {
                        const docSnap = await getDoc(docRef);
                        
                        if (isWFH) {
                            // Work from home day - show coffee shop
                            if (docSnap.exists()) {
                                const data = docSnap.data();
                                console.log(`${dayName} (${dateStr}) - Work From Home:`);
                                console.log(`  ☕ Location: ${data.dailyLocation?.name || data.afternoonLocation?.name || 'Not set'}`);
                                
                                // Show attendees if any
                                const wfhCount = data.attendees?.filter(a => a.session === 'workFromHome').length || 0;
                                if (wfhCount > 0) console.log(`     (${wfhCount} RSVP${wfhCount > 1 ? 's' : ''})`);
                            } else {
                                console.log(`${dayName} (${dateStr}) - Work From Home: Not seeded yet`);
                            }
                        } else {
                            // Office day
                            console.log(`${dayName} (${dateStr}) - Office Day:`);
                            console.log(`  🏢 ${officeLocation.name}`);
                        }
                        console.log('');
                    } catch (error) {
                        console.error(`Error checking ${dateStr}:`, error);
                    }
                }
            };
        }

        const todayStr = getTodayDateString();
        setCurrentDateStr(todayStr); // Store for use in render
        
        // Get the date that we're showing locations for (could be today or tomorrow)
        const displayDate = new Date(todayStr + 'T00:00:00');
        const isWeekend = displayDate.getDay() === 0 || displayDate.getDay() === 6; // 0 = Sunday, 6 = Saturday
        const isHolidayDate = isHoliday(todayStr);
        const isWFHDay = isWorkFromHomeDay(todayStr);
        
        const weekStart = getWeekStart(todayStr);

        if ((isWeekend || isHolidayDate) && !PREVIEW_MODE) {
            // It's the weekend or a holiday, don't show locations (unless in preview mode)
            setDailyLocation(null);
            setIsOfficeDay(false);
            setAttendees([]);
            setHappyHourAttendees([]);
            setIsLoading(false);
            return;
        }

        // If it's an office day (Mon, Tues, Thurs), show office location
        if (!isWFHDay && !PREVIEW_MODE) {
            setDailyLocation(officeLocation);
            setIsOfficeDay(true);
            setAttendees([]);
            setHappyHourAttendees([]);
            setIsLoading(false);
            return;
        }

        // Preview mode: Force show coffee shop for testing
        if (PREVIEW_MODE) {
            const dummyLocation = coffeeShops[0];
            setDailyLocation(dummyLocation);
            setIsOfficeDay(false);
            setAttendees([]);
            setHappyHourAttendees([]);
            setIsLoading(false);
            return;
        }

        // Only for work from home days (Wed/Fri), get coffee shop from Firestore
        const docRef = doc(db, 'daily_locations', todayStr);

        setIsLoading(true);
        const unsubscribe = onSnapshot(docRef, async (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                
                // Check if document has the dailyLocation field
                if (data.dailyLocation) {
                    // Set location from Firestore data
                    setDailyLocation(data.dailyLocation);
                    setIsOfficeDay(false);
                    setAttendees(data.attendees || []);
                    setHappyHourAttendees(data.happyHourAttendees || []);
                    setIsLoading(false);
                } else if (data.afternoonLocation) {
                    // Fallback: Use afternoon location from old structure
                    setDailyLocation(data.afternoonLocation);
                    setIsOfficeDay(false);
                    setAttendees(data.attendees || []);
                    setHappyHourAttendees(data.happyHourAttendees || []);
                    setIsLoading(false);
                } else {
                    // Document exists but has old structure, generate new location
                    const locations = generateDailyLocations(todayStr, []);
                    const location = locations.afternoonLocation || locations.morningLocation;
                    
                    setDailyLocation(location);
                    setIsOfficeDay(false);
                    setAttendees(data.attendees || []);
                    setHappyHourAttendees(data.happyHourAttendees || []);
                    setIsLoading(false);
                }
            } else {
                // Document doesn't exist, generate new location using weekly rotation
                // Get all used shops from this week to avoid duplicates
                try {
                    const usedShopsThisWeek = await getUsedShopsThisWeek(db, weekStart);
                    const locations = generateDailyLocations(todayStr, usedShopsThisWeek);
                    const location = locations.afternoonLocation || locations.morningLocation;
                    
                    // Set the location in the UI
                    setDailyLocation(location);
                    setIsOfficeDay(false);
                    setAttendees([]);
                    setHappyHourAttendees([]);
                    setIsLoading(false);
                    
                    // Automatically save the generated location to Firestore
                    try {
                        await setDoc(docRef, {
                            dailyLocation: location,
                            attendees: [],
                            happyHourAttendees: [],
                            createdAt: new Date().toISOString(),
                            weekStart: locations.weekStart,
                            autoGenerated: true
                        });
                        console.log(`Auto-generated and saved daily location for ${todayStr}`);
                    } catch (saveError) {
                        console.error("Error auto-saving daily location:", saveError);
                    }
                } catch (error) {
                    console.error("Error generating location:", error);
                    // Fallback to simple generation
                    const locations = generateDailyLocations(todayStr, []);
                    const location = locations.afternoonLocation || locations.morningLocation;
                    setDailyLocation(location);
                    setIsOfficeDay(false);
                    setAttendees([]);
                    setHappyHourAttendees([]);
                    setIsLoading(false);
                    
                    // Try to save fallback location too
                    try {
                        await setDoc(docRef, {
                            dailyLocation: location,
                            attendees: [],
                            happyHourAttendees: [],
                            createdAt: new Date().toISOString(),
                            weekStart: locations.weekStart,
                            autoGenerated: true
                        });
                        console.log(`Auto-generated and saved fallback location for ${todayStr}`);
                    } catch (saveError) {
                        console.error("Error auto-saving fallback location:", saveError);
                    }
                }
            }
        }, async (err) => {
            console.error("Snapshot listener error:", err);
            // Use fallback location with weekly rotation
            const locations = generateDailyLocations(todayStr, []);
            const location = locations.afternoonLocation || locations.morningLocation;
            setDailyLocation(location);
            setIsOfficeDay(false);
            setAttendees([]);
            setHappyHourAttendees([]);
            setIsLoading(false);
            
            // Try to save fallback location to Firestore
            try {
                const docRef = doc(db, 'daily_locations', todayStr);
                await setDoc(docRef, {
                    dailyLocation: location,
                    attendees: [],
                    happyHourAttendees: [],
                    createdAt: new Date().toISOString(),
                    weekStart: getWeekStart(todayStr),
                    autoGenerated: true
                });
                console.log(`Auto-generated and saved fallback location for ${todayStr} after error`);
            } catch (saveError) {
                console.error("Error auto-saving fallback location after error:", saveError);
            }
        });
        
        return () => unsubscribe();
    }, []);

    // --- Event Handlers ---
    const handleLogin = async () => {
        if (!auth) return;
        setError(null); // Clear any existing errors
        const provider = new GoogleAuthProvider();
        // Restrict to your company's domain
        provider.setCustomParameters({
            'hd': 'schaeferadvertising.com'
        });
        try {
            await signInWithPopup(auth, provider);
        } catch (error) {
            console.error("Login failed:", error);
            if (error.code === 'auth/popup-closed-by-user') {
                 setError("Login cancelled. Please try again.");
            } else if (error.message.includes("hd parameter")) {
                 setError("Please use a @schaeferadvertising.com email address to log in.");
            } else {
                 setError("An error occurred during login.");
            }
        }
    };

    const handleLogout = async () => {
        if (!auth) return;
        await signOut(auth);
    };

    const handleImGoing = async (session) => {
        if (!user || !db) return;
        setError(null); // Clear any existing errors
        
        // Skip time selection for happy hour - go directly to RSVP
        if (session === 'happyHour') {
            await handleHappyHourRSVP();
            return;
        }
        
        setSelectedSession(session);
        setShowTimeModal(true);
    };

    const handleHappyHourRSVP = async () => {
        if (!user || !db) return;
        setError(null); // Clear any existing errors
        
        const todayStr = getTodayDateString();
        const docRef = doc(db, 'daily_locations', todayStr);
        
        const userToAdd = {
            uid: user.uid,
            firstName: user.displayName?.split(' ')[0] || 'User',
            timestamp: new Date().toISOString(),
            session: 'happyHour'
        };

        try {
            // First try to update the document
            await updateDoc(docRef, {
                happyHourAttendees: arrayUnion(userToAdd)
            });
        } catch (error) {
            console.error("Error marking happy hour attendance:", error);
            
            // If document doesn't exist, create it with the user as first attendee
            if (error.message.includes("No document to update")) {
                try {
                    // Get used shops this week and generate locations using weekly rotation
                    const weekStart = getWeekStart(todayStr);
                    const usedShopsThisWeek = await getUsedShopsThisWeek(db, weekStart);
                    const locations = generateDailyLocations(todayStr, usedShopsThisWeek);
                    const location = locations.afternoonLocation || locations.morningLocation;
                    
                    await setDoc(docRef, {
                        dailyLocation: location,
                        attendees: [],
                        happyHourAttendees: [userToAdd],
                        createdAt: new Date().toISOString(),
                        weekStart: locations.weekStart
                    });
                } catch (createError) {
                    console.error("Error creating new daily location:", createError);
                    setError("Could not mark you as 'going'. Please try again.");
                }
            } else {
                setError("Could not mark you as 'going'. Please try again.");
            }
        }
    };

    const handleRemoveHappyHour = async () => {
        if (!user || !db) return;
        setError(null); // Clear any existing errors
        const todayStr = getTodayDateString();
        const docRef = doc(db, 'daily_locations', todayStr);
        
        try {
            // Remove the current user from happy hour attendees
            const updatedHappyHourAttendees = happyHourAttendees.filter(attendee => 
                attendee.uid !== user.uid
            );
            await updateDoc(docRef, {
                happyHourAttendees: updatedHappyHourAttendees
            });
        } catch (error) {
            console.error("Error removing happy hour attendance:", error);
            setError("Could not remove you from the list. Please try again.");
        }
    };

    const handleTimeSelection = async () => {
        if (!selectedTime) {
            setError("Please select a time.");
            return;
        }

        const todayStr = getTodayDateString();
        const docRef = doc(db, 'daily_locations', todayStr);
        
        const userToAdd = {
            uid: user.uid,
            firstName: user.displayName?.split(' ')[0] || 'User',
            timestamp: selectedTime,
            session: selectedSession
        };

        try {
            // First try to update the document
            await updateDoc(docRef, {
                attendees: arrayUnion(userToAdd)
            });
            setShowTimeModal(false);
            setSelectedTime('');
            setSelectedSession('');
        } catch (error) {
            console.error("Error marking attendance:", error);
            
            // If document doesn't exist, create it with the user as first attendee
            if (error.message.includes("No document to update")) {
                try {
                    // Get used shops this week and generate locations using weekly rotation
                    const weekStart = getWeekStart(todayStr);
                    const usedShopsThisWeek = await getUsedShopsThisWeek(db, weekStart);
                    const locations = generateDailyLocations(todayStr, usedShopsThisWeek);
                    const location = locations.afternoonLocation || locations.morningLocation;
                    
                    await setDoc(docRef, {
                        dailyLocation: location,
                        attendees: [userToAdd],
                        createdAt: new Date().toISOString(),
                        weekStart: locations.weekStart
                    });
                    setShowTimeModal(false);
                    setSelectedTime('');
                    setSelectedSession('');
                } catch (createError) {
                    console.error("Error creating new daily location:", createError);
                    setError("Could not mark you as 'going'. Please try again.");
                }
            } else {
                setError("Could not mark you as 'going'. Please try again.");
            }
        }
    };

    const handleRemoveMe = async (session) => {
        if (!user || !db) return;
        setError(null); // Clear any existing errors
        const todayStr = getTodayDateString();
        const docRef = doc(db, 'daily_locations', todayStr);
        
        try {
            // Remove the current user from attendees for the specific session
            const updatedAttendees = attendees.filter(attendee => 
                !(attendee.uid === user.uid && attendee.session === session)
            );
            await updateDoc(docRef, {
                attendees: updatedAttendees
            });
        } catch (error) {
            console.error("Error removing attendance:", error);
            setError("Could not remove you from the list. Please try again.");
        }
    };

    const isUserGoing = useMemo(() => {
        if (!user) return false;
        return attendees.some(attendee => attendee.uid === user.uid && attendee.session === 'workFromHome');
    }, [user, attendees]);

    const isUserGoingHappyHour = useMemo(() => {
        if (!user) return false;
        return happyHourAttendees.some(attendee => attendee.uid === user.uid);
    }, [user, happyHourAttendees]);

    const locationGoogleMapsUrl = dailyLocation ? 
        `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(dailyLocation.address)}` 
        : '#';
    const happyHourGoogleMapsUrl = happyHourConfig.location ? 
        `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(happyHourConfig.location.address)}` 
        : '#';

    // --- Render Logic ---
    return (
        <div className="bg-[#C02C00] min-h-screen font-montserrat antialiased text-gray-800">
            <div className="container mx-auto p-4">
                
                {/* Top Bar */}
                <div className="flex items-center justify-between py-3 px-4 bg-black/30 backdrop-blur-sm rounded-lg shadow-sm mb-4">
                    <div className="text-sm font-bold text-white font-montserrat">
                        Happy {currentDateStr ? new Date(currentDateStr + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long' }) : new Date().toLocaleDateString('en-US', { weekday: 'long' })} {currentDateStr ? new Date(currentDateStr + 'T00:00:00').toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' }) : new Date().toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' })}!
                    </div>

                    <div>
                        {isAuthReady && (
                            user && !user.isAnonymous ? (
                                <button onClick={handleLogout} className="bg-gradient-to-r from-white to-gray-200 text-black font-semibold py-1 px-3 rounded-lg shadow-md transform hover:scale-105 transition-all duration-300 text-sm">
                                    Logout
                                </button>
                            ) : (
                                <button onClick={handleLogin} className="bg-gradient-to-r from-white to-gray-200 text-black font-semibold py-1 px-3 rounded-lg shadow-md transform hover:scale-105 transition-all duration-300 text-sm">
                                    Login
                                </button>
                            )
                        )}
                    </div>
                </div>

                <header className="flex items-center justify-center py-6">
                    <img 
                        src="/images/find-my-brett-logo.gif" 
                        alt="Find My Brett" 
                        className="w-full max-w-sm md:max-w-md lg:max-w-xl h-auto object-contain"
                    />
                </header>

                {/* Description Text */}
                <div className="w-full mb-6">
                    
                    <p className="text-sm font-semibold text-white leading-relaxed text-center font-montserrat">
                        With Find my Brett, stay connected with your fellow squirrels! When working from home, find out what coffee shop Brett is hiding at. When in the office, you'll know where to find him.
                    </p>
                    
                    {/* Show indicator if we're displaying tomorrow's locations */}
                    {(() => {
                        const now = new Date();
                        const cstTime = new Date(now.toLocaleString("en-US", {timeZone: "America/Chicago"}));
                        const isAfter9PM = cstTime.getHours() >= 21;
                        
                        if (isAfter9PM && currentDateStr) {
                            // Get what today's date would be (without the 9 PM logic)
                            const todayCST = new Date(now.toLocaleString("en-US", {timeZone: "America/Chicago"}));
                            const year = todayCST.getFullYear();
                            const month = String(todayCST.getMonth() + 1).padStart(2, '0');
                            const day = String(todayCST.getDate()).padStart(2, '0');
                            const todayStr = `${year}-${month}-${day}`;
                            
                            // If currentDateStr is different from today's date, we're showing tomorrow
                            if (currentDateStr !== todayStr) {
                                return (
                                    <div className="mt-3 text-center">
                                        <p className="text-xs text-white/80 font-medium">
                                            📅 Showing tomorrow's locations (available after 9 PM CST)
                                        </p>
                                    </div>
                                );
                            }
                        }
                        return null;
                    })()}
                    
                </div>

                {/* Error Display */}
                {error && (
                    <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4 rounded-md" role="alert">
                        <p className="font-bold">Error</p>
                        <p>{error}</p>
                    </div>
                )}

                                {/* Main Content */}
                {isLoading ? (
                    <div className="text-center py-10">
                        <p className="text-lg text-white">Finding today's location...</p>
                    </div>
                ) : !dailyLocation ? (
                    // Weekend or holiday message
                    <div className="text-center py-20">
                        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 max-w-2xl mx-auto border border-white/20">
                            <p className="text-2xl font-semibold text-white leading-relaxed">
                                {(() => {
                                    const isHolidayDate = isHoliday(currentDateStr);
                                    if (isHolidayDate) {
                                        return "Our squirrels are taking a holiday break. No coffee spots today!";
                                    } else {
                                        return "Our squirrels are collecting more coffee beans for next week's spots. See you next week!";
                                    }
                                })()}
                            </p>
                        </div>
                    </div>
                ) : dailyLocation ? (
                    <div className={`grid grid-cols-1 ${isHappyHourDay(currentDateStr) ? 'lg:grid-cols-2' : ''} gap-6 max-w-2xl ${isHappyHourDay(currentDateStr) ? 'lg:max-w-6xl' : ''} mx-auto`}>
                        {/* Daily Location Card */}
                        <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-2xl overflow-hidden border border-amber-200 flex flex-col h-full">
                            {/* Google Map */}
                            <div className="w-full h-48">
                                <iframe
                                    title={`Map of ${dailyLocation.name}`}
                                    width="100%"
                                    height="100%"
                                    style={{ border: 0 }}
                                    loading="lazy"
                                    allowFullScreen
                                    referrerPolicy="no-referrer-when-downgrade"
                                    src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyB5j6gVqdoPGTVb-Llvl5nGsMvvKXn09GA&q=${encodeURIComponent(dailyLocation.address)}`}
                                />
                            </div>
                            
                            {/* Map View Simulation */}
                            <a href={locationGoogleMapsUrl} target="_blank" rel="noopener noreferrer" className="block bg-gradient-to-r from-[#fec601]/20 to-[#fec601]/40 p-4 text-center group hover:from-[#fec601]/60 hover:to-[#fec601]/80 transition-all duration-300">
                                 <div className="flex items-center justify-center text-black">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2 text-amber-500" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                                    </svg>
                                    <span className="font-semibold group-hover:underline">{dailyLocation.address}</span>
                                </div>
                                <p className="text-sm text-black mt-1 font-medium">Click to open in Google Maps</p>
                            </a>

                            <div className="p-6 flex-1 flex flex-col">
                                {/* Location Details */}
                                <h2 className="text-3xl font-semibold mb-3 text-gray-800 font-clarendon">{dailyLocation.name}</h2>
                                <p className="text-black-700 mb-6 leading-relaxed">{dailyLocation.description}</p>
                                
                                {/* Location Details List */}
                                <div className="mb-6">
                                    <ul className="space-y-2 text-black-700 text-sm">
                                        <li><span className="font-bold">Parking:</span> {dailyLocation.parking}</li>
                                        {!isOfficeDay && dailyLocation.plugs && (
                                            <li><span className="font-bold">Plugs:</span> {dailyLocation.plugs}</li>
                                        )}
                                        {dailyLocation.capacity && (
                                            <li><span className="font-bold">Capacity:</span> {dailyLocation.capacity}</li>
                                        )}
                                    </ul>
                                </div>
                            
                            {/* RSVP Section - Only for Work From Home Days */}
                            {!isOfficeDay && (
                                <div className="p-6 bg-white/50 rounded-xl border border-[#fec601] mt-auto">
                                    {/* Login prompt */}
                                    {(!user || (user && user.isAnonymous)) && isAuthReady && (
                                        <div className="text-center">
                                            <p className="font-semibold text-black">Ready to join the remote work crew?</p>
                                            <p className="text-gray-600 text-sm mb-3">Login to mark yourself as going and see who else is working from this coffee shop.</p>
                                            <button onClick={handleLogin} className="bg-gradient-to-r from-[#fec601] to-[#eab300] text-black font-semibold py-2 px-6 rounded-lg shadow-md transform hover:scale-105 transition-all duration-300">
                                                Login to Join
                                            </button>
                                        </div>
                                    )}
                                    
                                    {/* Logged in user content */}
                                    {user && !user.isAnonymous && (
                                        <>
                                            <h3 className="text-xl font-semibold mb-4 text-black font-clarendon">☕ Work From Home Day</h3>
                                            
                                            {/* Attendees Section */}
                                            <div className="mb-4">
                                                {attendees.filter(a => a.session === 'workFromHome').length > 0 ? (
                                                    <div className="flex flex-wrap gap-3">
                                                        {attendees
                                                            .filter(a => a.session === 'workFromHome')
                                                            .sort((a, b) => timeStringToMinutes(a.timestamp) - timeStringToMinutes(b.timestamp))
                                                            .map((attendee) => (
                                                            <div key={attendee.uid} className="bg-gradient-to-r from-[#e8b100]/80 to-[#e8b100] text-black font-semibold px-4 py-2 rounded-full text-sm shadow-md">
                                                                {attendee.firstName} {attendee.timestamp ? `(${attendee.timestamp})` : ''}
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <p className="text-gray-600 italic">Be the first to join today's work from home session!</p>
                                                )}
                                            </div>

                                            {/* Action Button */}
                                            <div className="mt-4">
                                                {isUserGoing ? (
                                                   <button onClick={() => handleRemoveMe('workFromHome')} className="w-full bg-gradient-to-r from-red-500 to-red-600 text-white font-bold py-3 px-6 rounded-xl shadow-lg hover:from-red-600 hover:to-red-700 transform hover:scale-105 transition-all duration-300">
                                                    Remove Me
                                                </button>
                                            ) : (
                                                <button onClick={() => handleImGoing('workFromHome')} className="w-full bg-gradient-to-r from-[#fec601] to-[#eab300] text-black font-bold py-3 px-6 rounded-xl shadow-xl transform hover:scale-105 transition-all duration-300">
                                                    I'm Going!
                                                </button>
                                                )}
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}

                            </div>
                        </div>

                        {/* Happy Hour Card - Only show if it's happy hour day */}
                        {isHappyHourDay(currentDateStr) && (
                            <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-2xl overflow-hidden border border-purple-200 flex flex-col h-full">
                                {/* Google Map */}
                                <div className="w-full h-48">
                                    <iframe
                                        title={`Map of ${happyHourConfig.location.name}`}
                                        width="100%"
                                        height="100%"
                                        style={{ border: 0 }}
                                        loading="lazy"
                                        allowFullScreen
                                        referrerPolicy="no-referrer-when-downgrade"
                                        src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyB5j6gVqdoPGTVb-Llvl5nGsMvvKXn09GA&q=${encodeURIComponent(happyHourConfig.location.address)}`}
                                    />
                                </div>
                                
                                {/* Map View Simulation */}
                                <a href={happyHourGoogleMapsUrl} target="_blank" rel="noopener noreferrer" className="block bg-gradient-to-r from-purple-200 to-purple-300 p-4 text-center group hover:from-purple-300 hover:to-purple-400 transition-all duration-300">
                                     <div className="flex items-center justify-center text-black">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2 text-purple-500" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                                        </svg>
                                        <span className="font-semibold group-hover:underline">{happyHourConfig.location.address}</span>
                                    </div>
                                    <p className="text-sm text-black mt-1 font-medium">Click to open in Google Maps</p>
                                </a>

                                <div className="p-6 flex-1 flex flex-col">
                                    {/* Location Details */}
                                    <h2 className="text-3xl font-semibold mb-3 text-gray-800 font-clarendon">Happy Hour: {happyHourConfig.location.name}</h2>
                                    <p className="text-black-700 mb-6 leading-relaxed">{happyHourConfig.location.description}</p>
                                    
                                    {/* Location Details List */}
                                    <div className="mb-6">
                                        <ul className="space-y-2 text-black-700 text-sm">
                                            <li><span className="font-bold">Time:</span> {happyHourConfig.location.time}</li>
                                            <li><span className="font-bold">Parking:</span> {happyHourConfig.location.parking}</li>
                                            <li><span className="font-bold">Capacity:</span> {happyHourConfig.location.capacity}</li>
                                        </ul>
                                    </div>
                                    
                                    {/* Happy Hour Session */}
                                    <div className="p-6 bg-white/50 rounded-xl border border-purple-300 mt-auto">
                                        {/* Login prompt for happy hour */}
                                        {(!user || (user && user.isAnonymous)) && isAuthReady && (
                                            <div className="text-center">
                                                <p className="font-semibold text-black">Ready to join the happy hour crew?</p>
                                                <p className="text-gray-600 text-sm mb-3">Login to mark yourself as going and see who else is attending.</p>
                                                <button onClick={handleLogin} className="bg-gradient-to-r from-purple-500 to-purple-600 text-white font-semibold py-2 px-6 rounded-lg shadow-md transform hover:scale-105 transition-all duration-300">
                                                    Login to Join
                                                </button>
                                            </div>
                                        )}
                                        
                                        {/* Logged in user content for happy hour */}
                                        {user && !user.isAnonymous && (
                                            <>
                                                <h3 className="text-xl font-semibold mb-4 text-black font-clarendon">🌙 Happy Hour</h3>
                                                
                                                {/* Happy Hour Attendees Section */}
                                                <div className="mb-4">
                                                    {happyHourAttendees.length > 0 ? (
                                                        <div className="flex flex-wrap gap-3">
                                                            {happyHourAttendees.map((attendee) => (
                                                                <div key={attendee.uid} className="bg-gradient-to-r from-purple-400/80 to-purple-500 text-white font-semibold px-4 py-2 rounded-full text-sm shadow-md">
                                                                    {attendee.firstName}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <p className="text-gray-600 italic">Be the first to join the happy hour!</p>
                                                    )}
                                                </div>

                                                {/* Happy Hour Action Button */}
                                                <div className="mt-4">
                                                    {isUserGoingHappyHour ? (
                                                       <button onClick={handleRemoveHappyHour} className="w-full bg-gradient-to-r from-red-500 to-red-600 text-white font-bold py-3 px-6 rounded-xl shadow-lg hover:from-red-600 hover:to-red-700 transform hover:scale-105 transition-all duration-300">
                                                            Remove Me
                                                        </button>
                                                ) : (
                                                    <button onClick={() => handleImGoing('happyHour')} className="w-full bg-gradient-to-r from-purple-500 to-purple-600 text-white font-bold py-3 px-6 rounded-xl shadow-xl transform hover:scale-105 transition-all duration-300">
                                                        I'm Going!
                                                    </button>
                                                    )}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    !error && <div className="text-center py-10"><p>No locations set for today.</p></div>
                )}
                
                {/* Time Selection Modal */}
                {showTimeModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl">
                            <h3 className="text-xl font-semibold mb-4 text-gray-800">
                                What time are you planning to arrive?
                            </h3>
                            <div className="mb-6">
                                <select 
                                    value={selectedTime} 
                                    onChange={(e) => setSelectedTime(e.target.value)}
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#febc1a] focus:border-transparent"
                                >
                                    <option value="">Select a time...</option>
                                    {generateTimeOptions(selectedSession).map((time) => (
                                        <option key={time} value={time}>{time}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex gap-3">
                                <button 
                                    onClick={() => {
                                        setShowTimeModal(false);
                                        setSelectedTime('');
                                        setSelectedSession('');
                                    }}
                                    className="flex-1 bg-gray-300 text-black-700 font-semibold py-3 px-4 rounded-lg hover:bg-gray-400 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button 
                                    onClick={handleTimeSelection}
                                    className="flex-1 bg-gradient-to-r from-[#fec601] to-[#eab300] text-black font-semibold py-3 px-4 rounded-lg shadow-md transform hover:scale-105 transition-all duration-300"
                                >
                                    Confirm
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                
                <footer className="text-center text-white text-xs mt-8 pb-4">
                     <img src="/images/schaefer-building.svg" alt="Schaefer Advertising" className="w-20 h-20 mx-auto p-4" />
                    <p>Made with coffee for the Schaefer team.</p>
                </footer>
            </div>
        </div>
    );
}


// --- Main App Component with Routing ---
export default function App() {
    return (
        <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/stats" element={<Stats />} />
        </Routes>
    );
} 